import { useEffect, useRef, useState } from 'react';
import { useGetAtsApplicationsByJobLazyQuery } from 'generated-graphql/hooks/apollo-hooks';
import { fetchAllPaginatedItems } from 'core/utils/apollo/fetchAllPaginatedItems';
import { logger } from 'core/utils/logger';
import { getAssociatedCandidateProfileIds } from 'modules/ats/utils/getAssociatedCandidateProfileIds';

export type ReconcileCandidate = {
  candidateProfileId: string;
  atsCandidateId?: string | null;
};

type UseAtsJobAlreadyAssociatedCandidateIdsProps = {
  atsJobId: string | null;
  candidates: ReconcileCandidate[];
};

/**
 * For a chosen ATS job, paginates through every existing application on that
 * job and returns which of the given candidates are already associated with it,
 * so callers can exclude them from a bulk "add to job" submit.
 *
 * A candidate counts as associated when the job has an application on their
 * exact `candidateProfileId` OR on their `atsCandidateId`. The `atsCandidateId`
 * fallback catches the same person carrying a duplicate CandidateProfile — an
 * application can be linked to a different profile row for the same ATS
 * candidate — and it's a safe extra key: two different people never share one.
 */
export const useAtsJobAlreadyAssociatedCandidateIds = ({
  atsJobId,
  candidates
}: UseAtsJobAlreadyAssociatedCandidateIdsProps) => {
  const [associatedCandidateProfileIds, setAssociatedCandidateProfileIds] =
    useState<string[]>([]);
  const [isReconciling, setIsReconciling] = useState(false);
  const [isReconcileComplete, setIsReconcileComplete] = useState(true);
  const [reconcileError, setReconcileError] = useState<Error | null>(null);
  const [fetchAtsApplicationsByJob] = useGetAtsApplicationsByJobLazyQuery();
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Stable, content-based key so the effect re-runs only when the selected
  // candidates (by either identifier) or the job actually change.
  const candidatesKey = candidates
    .map(
      (candidate) =>
        `${candidate.candidateProfileId}:${candidate.atsCandidateId ?? ''}`
    )
    .join(',');

  useEffect(() => {
    // Always clear any previous job's result up-front so a stale exclusion
    // set from a prior `atsJobId` is never applied to the newly selected job.
    setAssociatedCandidateProfileIds([]);
    setReconcileError(null);
    setIsReconcileComplete(true);

    if (!atsJobId || candidates.length === 0) {
      setIsReconciling(false);
      return;
    }

    setIsReconciling(true);

    let hasPageError = false;

    const fetchPage = async (nextToken?: string | null) => {
      try {
        const { data, error } = await fetchAtsApplicationsByJob({
          variables: { atsJobId, nextToken: nextToken ?? null }
        });
        // `useLazyQuery`'s execute function resolves (rather than rejects)
        // with an `error` field on network/GraphQL errors, unlike
        // `ApolloClient.query`, so it must be checked explicitly here.
        if (error) {
          throw error;
        }
        return data?.getAtsApplicationsByJob;
      } catch (error) {
        hasPageError = true;
        logger.error(error, {
          message:
            'Failed to fetch a page of ATS applications while reconciling already-associated candidates',
          atsJobId
        });
        return undefined;
      }
    };

    let isCancelled = false;

    const failReconcile = (error: unknown) => {
      logger.error(error, {
        message: 'Failed to reconcile already-associated candidates',
        atsJobId
      });
      // Never present an incomplete view as the authoritative exclusion set.
      setAssociatedCandidateProfileIds([]);
      setIsReconcileComplete(false);
      setReconcileError(
        error instanceof Error
          ? error
          : new Error(
              `Failed to fully reconcile already-associated candidates for ATS job ${atsJobId}`
            )
      );
      setIsReconciling(false);
    };

    const reconcile = async () => {
      try {
        // `fetchAllPaginatedItems` itself can throw (e.g. its max-pages or
        // repeated-cursor guards), separately from a per-page fetch failure.
        const applications = await fetchAllPaginatedItems(fetchPage);
        if (isCancelled || !isMountedRef.current) return;

        if (hasPageError) {
          failReconcile(
            new Error(
              `Failed to fully reconcile already-associated candidates for ATS job ${atsJobId}`
            )
          );
          return;
        }

        setAssociatedCandidateProfileIds(
          getAssociatedCandidateProfileIds(applications, candidates)
        );
        setIsReconcileComplete(true);
        setIsReconciling(false);
      } catch (error) {
        if (isCancelled || !isMountedRef.current) return;
        failReconcile(error);
      }
    };

    reconcile();

    return () => {
      isCancelled = true;
    };
    // `candidates` is compared by content via `candidatesKey`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [atsJobId, candidatesKey]);

  return {
    associatedCandidateProfileIds,
    isReconciling,
    isReconcileComplete,
    reconcileError
  };
};
